

import React, { useState, useEffect } from 'react';
import { useRegion } from '../src/contexts/RegionContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Listing, User, Favorite } from '../types';
import { useTranslation } from 'react-i18next';
import ListingCard from './ListingCard';
import ListingSkeleton from './skeletons/ListingSkeleton';
import BoostModal from './BoostModal';
import FilterSidebar from './FilterSidebar';
import { searchClient } from '../lib/algolia';
import { InstantSearch, useSearchBox, Configure, useInstantSearch, useInfiniteHits, useRefinementList, useClearRefinements } from 'react-instantsearch';
import { history } from 'instantsearch.js/es/lib/routers';
import { useAnalytics } from '../hooks/useAnalytics';
import PageHeader from './ui/PageHeader';
import MobileFilterSheet from './MobileFilterSheet';
import SwipeableFilterBar from './mobile/SwipeableFilterBar';
import MobileSortBar from './mobile/MobileSortBar';
import NoResults from './ui/NoResults';
import DiscoveryLayout from './discovery/DiscoveryLayout';
import FlashSalesBanner from './discovery/FlashSalesBanner';
import PersonalizedSearch from './discovery/PersonalizedSearch';
import CategoryBrowser from './discovery/CategoryBrowser';

interface MarketplaceProps {
  users: User[];
  favorites: Favorite[];
  currentUser: User | null;
  onToggleFavorite: (listingId: string) => void;
  initialMode?: 'listings' | 'users';
  initialFollowingFeed?: boolean;
}

const AtmosphericMotif = () => {
  const { config } = useRegion();
  return (
    <div className="fixed top-0 right-0 w-full h-full pointer-events-none z-[-1] overflow-hidden opacity-[0.03] dark:opacity-[0.05] transition-opacity duration-1000">
      <div className="absolute top-[-5%] right-[-5%] rotate-12 scale-150 select-none">
        <span className="text-[35rem] md:text-[50rem] font-black leading-none drop-shadow-2xl">{config.theme.motifIcon}</span>
      </div>
    </div>
  );
};

const BoutiqueWelcome = () => {
  const { config } = useRegion();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className="mx-4 lg:mx-0 mb-10 p-10 rounded-[4rem] bg-brand-primary relative overflow-hidden shadow-premium border border-white/5"
    >
      <div className="relative z-10">
        <motion.h2
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-5xl md:text-6xl font-black italic tracking-tighter uppercase leading-none text-brand-accent"
        >
          {config.theme.vibe}
        </motion.h2>
        <motion.p
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-xs md:text-sm font-black text-brand-clay/80 tracking-[0.5em] uppercase mt-4"
        >
          The {config.theme.landmark} Collection
        </motion.p>
      </div>
      <div className="absolute top-[-20%] right-[-10%] opacity-10 select-none pointer-events-none">
        <span className="text-[15rem] md:text-[20rem]">{config.theme.motifIcon}</span>
      </div>

      {/* Decorative Light Rays */}
      <div className="absolute inset-0 bg-gradient-to-tr from-brand-accent/5 to-transparent pointer-events-none" />
    </motion.div>
  );
};

// --- Internal Search Components to Access Hook State ---

const CategoryRefinementTrigger = ({ categoryId, subCategoryId, productTypeId, onTriggered }: { categoryId: string | null, subCategoryId?: string | null, productTypeId?: string | null, onTriggered: () => void }) => {
  const { items: catItems, refine: refineCategory } = useRefinementList({ attribute: 'categoryId' });
  const { items: subItems, refine: refineSubCategory } = useRefinementList({ attribute: 'subCategoryId' });
  const { items: typeItems, refine: refineProductType } = useRefinementList({ attribute: 'productTypeId' });

  const { refine: clearCategories } = useClearRefinements({
    includedAttributes: [
      'categoryId', 'subCategoryId', 'productTypeId', 'brand', 'model',
      'attributes.brand', 'attributes.model', 'attributes.km', 'attributes.year',
      'attributes.fuel', 'attributes.gear', 'attributes.rooms', 'attributes.floor',
      'attributes.size', 'attributes.color'
    ]
  });

  useEffect(() => {
    if (categoryId) {
      // Clear all category-related refinements first
      clearCategories();

      // Delay to ensure clearing is processed by InstantSearch internal state
      const timer = setTimeout(() => {
        refineCategory(categoryId);
        if (subCategoryId) {
          refineSubCategory(subCategoryId);
        }
        if (productTypeId) {
          refineProductType(productTypeId);
        }
        onTriggered();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [categoryId, subCategoryId, productTypeId]); // Minimal deps to avoid infinite loops

  return null;
};

const BrandRefinementTrigger = ({ brand, onTriggered }: { brand: string | null, onTriggered: () => void }) => {
  const { refine } = useRefinementList({ attribute: 'brand' });

  useEffect(() => {
    if (brand) {
      refine(brand);
      onTriggered();
    }
  }, [brand, refine, onTriggered]);

  return null;
};

const URLSyncTrigger = ({ onSyncComplete }: { onSyncComplete: (params: { q?: string, cat?: string }) => void }) => {
  const [searchParams] = useSearchParams();
  const { refine: refineQuery } = useSearchBox();
  const { refine: refineCategory } = useRefinementList({ attribute: 'categoryId' });
  const { refine: clearAll } = useClearRefinements();

  useEffect(() => {
    const q = searchParams.get('q');
    const cat = searchParams.get('category');

    // 1. Clear everything if we're at the base URL (user might have clicked logo)
    if (!q && !cat) {
      if (refineQuery) refineQuery('');
      if (clearAll) clearAll();
      return;
    }

    // 2. Clear first for a clean slate from URL
    clearAll();

    // 3. Apply new filters
    const timer = setTimeout(() => {
      if (q) refineQuery(q);
      if (cat) refineCategory(cat);
      onSyncComplete({ q: q || undefined, cat: cat || undefined });
    }, 50);

    return () => clearTimeout(timer);
  }, [searchParams, clearAll, refineQuery, refineCategory, onSyncComplete]);

  return null;
};

const CustomSearchBox = ({ onToggleFilters, searchMode, hasFollowings }: { onToggleFilters: () => void, searchMode: 'listings' | 'users', hasFollowings: boolean }) => {
  const { query, refine } = useSearchBox();
  const { t } = useTranslation('marketplace');
  const navigate = useNavigate();
  const [localQuery, setLocalQuery] = useState(query || '');

  // Sync upstream query changes
  useEffect(() => {
    if (query !== localQuery) {
      setLocalQuery(query || '');
    }
  }, [query]);

  // Debounce Effect for typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query !== localQuery) {
        refine(localQuery);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [localQuery, refine, query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    refine(localQuery);
    (document.activeElement as HTMLElement)?.blur();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
      className="space-y-6 mb-8 px-4 lg:px-0"
    >
      <div className="flex gap-3">
        <div className="relative flex-1 group">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none">
            <svg className="w-5 h-5 text-gray-300 dark:text-gray-700 group-focus-within:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </div>
          <input
            type="text"
            placeholder={searchMode === 'listings' ? t('search.placeholder') : t('search.userPlaceholder')}
            className="w-full pl-12 pr-12 py-4 bg-brand-white border border-gray-100 dark:border-gray-800 rounded-2xl font-bold text-gray-900 dark:text-white outline-none focus:border-brand-accent/40 focus:ring-4 focus:ring-brand-accent/5 transition-all text-sm shadow-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 cursor-pointer"
            value={localQuery}
            readOnly
            onClick={() => navigate('/search')}
          />
          {localQuery && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                refine('');
                navigate('/');
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-rose-500 transition-colors active:scale-90"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const HitsGrid = ({
  currentUser,
  setSelectedBoostListing,
  searchMode
}: {
  currentUser: User | null,
  setSelectedBoostListing: (l: Listing) => void,
  searchMode: 'listings' | 'users'
}) => {
  const { hits, showMore, isLastPage } = useInfiniteHits();
  const { status } = useInstantSearch();
  const { query } = useSearchBox();
  const { t } = useTranslation('marketplace');

  if (status === 'loading' || status === 'stalled') {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {[...Array(12)].map((_, i) => (
          <ListingSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (hits.length === 0) {
    return (
      <NoResults
        searchTerm={query}
      />
    );
  }

  // Display Users
  if (searchMode === 'users') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {hits.map((hit: any) => {
          if (!hit.name && !hit.username) return null;

          return (
            <Link to={`/profile/${hit.objectID}`} key={hit.objectID} className="flex items-center gap-4 p-4 bg-brand-white rounded-2xl border border-gray-100 dark:border-gray-800 hover:shadow-xl hover:border-brand-accent/40 transition-all group shadow-sm">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-50 dark:bg-gray-800">
                <img src={hit.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(hit.name || 'User')}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform" alt="" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">{hit.name || t('user.anonymous')}</h4>
                <p className="text-xs text-gray-400 dark:text-gray-500 font-black uppercase tracking-tighter">@{hit.username || 'username'}</p>
              </div>
            </Link>
          );
        })}
      </div>
    );
  }

  // Display Listings (Default)
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        <AnimatePresence mode="popLayout">
          {hits.map((hit: any, index: number) => {
            const listing: Listing = {
              id: hit.objectID,
              title: hit.title,
              description: hit.description,
              price: hit.price,
              imageUrls: hit.imageUrls || hit.image_urls || [],
              status: hit.status,
              sellerId: hit.sellerId || hit.seller_id,
              categoryId: hit.categoryId,
              createdAt: hit.created_at || hit.createdAt,
              subCategoryId: hit.subCategoryId,
              productTypeId: hit.productTypeId,
              // 🛡️ Ensure attributes are correctly typed and keys are standardized
              attributes: Object.fromEntries(
                Object.entries(hit.attributes || {}).map(([k, v]) => {
                  const key = k.toLowerCase().replace(/\s+/g, '_');
                  // 🚀 Convert likely numeric fields to numbers for range filtering
                  const numericKeys = ['year', 'km', 'price', 'rooms', 'floor', 'power', 'weight', 'capacity', 'cc'];
                  const val = (numericKeys.includes(key) && v && !isNaN(Number(v))) ? Number(v) : v;
                  return [key, val === undefined ? null : val];
                })
              ),
              category: hit.category,
              type: hit.type,
              condition: hit.condition,
              boostedUntil: hit.boostedUntil,
              cartCount: hit.cartCount || hit.cart_count || 0
            } as Listing;

            return (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{
                  duration: 0.6,
                  delay: (index % 12) * 0.08,
                  ease: [0.16, 1, 0.3, 1]
                }}
                key={listing.id}
                className="relative group h-full"
              >
                <ListingCard listing={listing} priority={index < 4} />
                {currentUser?.id === listing.sellerId && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedBoostListing(listing);
                    }}
                    className="absolute bottom-4 right-4 z-20 px-4 py-2 bg-gray-800 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-premium opacity-0 group-hover:opacity-100 transition-all hover:scale-110 active:scale-95 flex items-center gap-2 border border-gray-700"
                  >
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                    {t('listingCard.boost')}
                  </button>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {!isLastPage && (
        <motion.button
          onClick={showMore}
          className="w-full mt-8 py-4 bg-brand-white border border-gray-100 dark:border-gray-800 rounded-2xl text-sm font-bold text-gray-900 dark:text-white hover:border-brand-accent/40 transition-all flex items-center justify-center gap-2 shadow-sm"
        >
          {t('loadMore')}
        </motion.button>
      )}
    </>
  );
};

const MobileFilterController = ({ onOpenFilterModal }: { onOpenFilterModal: () => void }) => {
  const { items: catItems } = useRefinementList({ attribute: 'categoryId' });
  const { query } = useSearchBox();

  const selectedCategory = catItems.find(i => i.isRefined)?.value || null;
  const isSearchActive = (query && query.trim().length > 0) || (selectedCategory !== null && selectedCategory !== 'all');

  return (
    <div className="px-4 space-y-2">
      <MobileCategoryFilter onOpenFilterModal={onOpenFilterModal} />
      {isSearchActive && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <MobileSortBar onOpenFilter={onOpenFilterModal} />
        </motion.div>
      )}
    </div>
  );
};

const MobileCategoryFilter = ({ onOpenFilterModal }: { onOpenFilterModal: () => void }) => {
  const { items, refine: refineCat } = useRefinementList({ attribute: 'categoryId' });
  const { refine: clearCategories } = useClearRefinements({
    includedAttributes: [
      'categoryId', 'subCategoryId', 'productTypeId', 'brand', 'model',
      'attributes.brand', 'attributes.model', 'attributes.km', 'attributes.year',
      'attributes.fuel', 'attributes.gear', 'attributes.rooms', 'attributes.floor',
      'attributes.size', 'attributes.color'
    ]
  });

  const selectedCategory = items.find(i => i.isRefined)?.value || null;

  return (
    <SwipeableFilterBar
      selectedCategory={selectedCategory}
      onSelectCategory={(id) => {
        // Broad clear first
        clearCategories();

        if (id && id !== 'all') {
          setTimeout(() => refineCat(id), 0);
        }
      }}
      onOpenFilterModal={onOpenFilterModal}
    />
  );
};

const Marketplace: React.FC<MarketplaceProps> = ({
  users,
  favorites,
  currentUser,
  onToggleFavorite,
  initialMode = 'listings',
  initialFollowingFeed = false
}) => {
  const { t } = useTranslation('marketplace');
  const navigate = useNavigate();
  const [selectedBoostListing, setSelectedBoostListing] = useState<Listing | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchParams] = useSearchParams();

  // Discovery State
  const [discoveryTab, setDiscoveryTab] = useState<'for-you' | 'categories' | 'following'>('for-you');
  const [pendingCategory, setPendingCategory] = useState<{ cat: string, sub?: string, type?: string } | null>(null);
  const [pendingBrand, setPendingBrand] = useState<string | null>(null);

  const [searchMode, setSearchMode] = useState<'listings' | 'users'>(initialMode);
  const [isFollowingFeed, setIsFollowingFeed] = useState(initialFollowingFeed);

  const { track } = useAnalytics();

  useEffect(() => {
    track('marketplace_viewed', { mode: searchMode });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={searchMode}
      routing={{
        router: history({ cleanUrlOnDispose: false }),
        stateMapping: {
          stateToRoute(uiState) {
            const indexUiState = uiState[searchMode];
            return {
              q: indexUiState.query,
              category: indexUiState.refinementList?.categoryId?.[0],
              sub: indexUiState.refinementList?.subCategoryId?.[0],
              type: indexUiState.refinementList?.productTypeId?.[0],
              page: indexUiState.page,
            };
          },
          routeToState(routeState: any) {
            return {
              [searchMode]: {
                query: routeState.q,
                refinementList: {
                  categoryId: routeState.category ? [routeState.category] : [],
                  subCategoryId: routeState.sub ? [routeState.sub] : [],
                  productTypeId: routeState.type ? [routeState.type] : [],
                },
                page: routeState.page,
              },
            };
          },
        },
      }}
      future={{ preserveSharedStateOnUnmount: true }}
    >
      <AtmosphericMotif />
      <Configure
        hitsPerPage={24}
        filters={
          searchMode === 'users' ? undefined :
            isFollowingFeed && currentUser?.followingIds?.length
              ? `status:ACTIVE AND (${currentUser.followingIds.map(id => `sellerId:${id}`).join(' OR ')})`
              : 'status:ACTIVE'
        }
      />

      <CategoryRefinementTrigger
        categoryId={pendingCategory?.cat || null}
        subCategoryId={pendingCategory?.sub || null}
        productTypeId={pendingCategory?.type || null}
        onTriggered={() => setPendingCategory(null)}
      />

      <BrandRefinementTrigger
        brand={pendingBrand}
        onTriggered={() => setPendingBrand(null)}
      />

      <URLSyncTrigger
        onSyncComplete={(params) => {
          if ((params.cat || params.q) && discoveryTab !== 'for-you') {
            setDiscoveryTab('for-you');
          }
        }}
      />

      <div className="space-y-6">
        <PageHeader title={t('title')} />

        <BoutiqueWelcome />

        <section className="flex items-center gap-4">
          <div className="flex-1">
            <CustomSearchBox
              onToggleFilters={() => setMobileFiltersOpen(true)}
              searchMode={searchMode}
              hasFollowings={!!currentUser?.followingIds?.length}
            />
          </div>
        </section>

        {searchMode === 'listings' && (
          <DiscoveryLayout
            activeTab={discoveryTab}
            onTabChange={(tab) => {
              if (tab !== 'for-you') {
                navigate('/'); // Clear URL params when switching to other tabs
              }
              setDiscoveryTab(tab);
            }}
          >
            {discoveryTab === 'categories' ? (
              <CategoryBrowser
                onSelectCategory={(cat, sub, type) => {
                  setPendingCategory({ cat, sub, type });
                  setDiscoveryTab('for-you');
                }}
              />
            ) : (
              <div className="space-y-6">
                {discoveryTab === 'for-you' && <FlashSalesBanner />}

                {discoveryTab === 'following' && !currentUser?.followingIds?.length && (
                  <div className="bg-brand-white backdrop-blur-3xl rounded-[4rem] py-24 text-center border border-gray-100 dark:border-white/5 mx-4 relative overflow-hidden group shadow-premium">
                    {/* Animated Background Glow */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-brand-terracotta/10 rounded-full blur-[100px] group-hover:bg-brand-terracotta/20 transition-all duration-1000" />

                    <div className="relative z-10">
                      <motion.div
                        animate={{
                          y: [0, -20, 0],
                          scale: [1, 1.1, 1],
                          rotate: [0, 5, -5, 0]
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                        className="text-7xl mb-10 inline-block drop-shadow-2xl filter saturate-[0.8] brightness-110"
                      >
                        🤝
                      </motion.div>

                      <h3 className="text-2xl font-black text-gray-900 dark:text-white tracking-tighter uppercase italic">{t('discovery.noFollowings')}</h3>
                      <p className="text-gray-400 dark:text-gray-500 text-sm mt-3 font-medium max-w-xs mx-auto leading-relaxed">{t('discovery.noFollowingsDesc')}</p>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setDiscoveryTab('for-you')}
                        className="mt-10 bg-gray-950 dark:bg-white text-white dark:text-gray-950 text-[10px] font-black uppercase tracking-[0.3em] px-10 py-5 rounded-[2rem] shadow-2xl hover:shadow-brand-terracotta/20 transition-all"
                      >
                        {t('discovery.startExploring')}
                      </motion.button>
                    </div>
                  </div>
                )}

                {(discoveryTab === 'for-you' || (discoveryTab === 'following' && currentUser?.followingIds?.length)) && (
                  <>
                    <MobileFilterController
                      onOpenFilterModal={() => setMobileFiltersOpen(true)}
                    />
                    <div className="flex gap-8 items-start px-4">
                      <div className="hidden lg:block sticky top-24">
                        <FilterSidebar />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Configure
                          filters={
                            discoveryTab === 'following'
                              ? `status:ACTIVE AND marketRegion:${useRegion().region} AND (${currentUser?.followingIds?.map(id => `sellerId:${id}`).join(' OR ')})`
                              : `status:ACTIVE AND marketRegion:${useRegion().region}`
                          }
                        />
                        <HitsGrid
                          currentUser={currentUser}
                          setSelectedBoostListing={setSelectedBoostListing}
                          searchMode={searchMode}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </DiscoveryLayout>
        )}

        {searchMode === 'users' && (
          <div className="px-4">
            <HitsGrid
              currentUser={currentUser}
              setSelectedBoostListing={setSelectedBoostListing}
              searchMode={searchMode}
            />
          </div>
        )}
      </div>

      <MobileFilterSheet isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} />

      {selectedBoostListing && (
        <BoostModal listing={selectedBoostListing} isOpen={!!selectedBoostListing} onClose={() => setSelectedBoostListing(null)} onSuccess={() => window.location.reload()} />
      )}

    </InstantSearch>
  );
};

export default Marketplace;
